import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { telephonyService } from '../services/telephony'
import type { CallLog, CallAnalysis, Reminder } from '../../../types'
import { CallSource } from '../../../types'
import { DISPOSITION_LABELS } from '../../../lib/constants'
import { authService } from '../../identity/services/auth'
import { UserRole } from '../../../types'
import {
  Phone, Bell, RefreshCw, ChevronDown, ChevronUp,
  Terminal, MessageSquare, BarChart2, Lightbulb, RotateCcw,
} from 'lucide-react'
import { useWindowWidth } from '../../../lib/useWindowWidth'

const PAYLOAD_PRIMARY_KEYS = ['CallSid', 'Status', 'EventType', 'ConversationDuration', 'RecordingUrl', 'StartTime', 'EndTime']

const SENTIMENT_STYLES: Record<string, { label: string; cls: string }> = {
  positive: { label: 'Positive', cls: 'badge-success' },
  neutral:  { label: 'Neutral',  cls: 'badge-neutral' },
  negative: { label: 'Negative', cls: 'badge-danger'  },
}

// ---------------------------------------------------------------------------
// Transcript — chat bubble view
// ---------------------------------------------------------------------------

function TranscriptPanel({ transcript }: { transcript: string }) {
  const lines = transcript
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)

  const parsed = lines.map(line => {
    if (line.startsWith('Agent:'))
      return { role: 'agent' as const, text: line.slice(6).trim() }
    if (line.startsWith('Customer:'))
      return { role: 'customer' as const, text: line.slice(9).trim() }
    return { role: 'agent' as const, text: line }
  })

  return (
    <div
      className="px-4 pb-4 pt-3"
      style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}
    >
      <p className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
        <MessageSquare className="h-3 w-3" /> Transcript
      </p>
      <div className="space-y-2">
        {parsed.map((msg, i) => {
          const isAgent = msg.role === 'agent'
          return (
            <div key={i} className={`flex items-end gap-2 ${isAgent ? '' : 'flex-row-reverse'}`}>
              <div
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium"
                style={
                  isAgent
                    ? { background: 'var(--brand-50)', color: 'var(--brand-700)' }
                    : { background: 'var(--surface-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }
                }
              >
                {isAgent ? 'A' : 'C'}
              </div>
              <div
                className="max-w-[72%] px-3 py-2 text-sm"
                style={{
                  background: isAgent ? 'var(--brand-50)' : 'var(--surface-default)',
                  color: isAgent ? 'var(--brand-800)' : 'var(--text-primary)',
                  border: isAgent ? 'none' : '1px solid var(--border-default)',
                  borderRadius: isAgent
                    ? '12px 12px 12px 3px'
                    : '12px 12px 3px 12px',
                  lineHeight: '1.55',
                }}
              >
                {msg.text}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Analysis — scorecard view
// ---------------------------------------------------------------------------

function ScoreBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ width: 72, background: 'var(--border-default)' }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${(value / 5) * 100}%`, background: 'var(--brand-primary)' }}
        />
      </div>
      <span className="text-xs font-medium w-4 text-right" style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  )
}

function MetricCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-lg px-3 py-2.5"
      style={{ background: 'var(--surface-muted)' }}
    >
      <p className="text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
      <div>{children}</div>
    </div>
  )
}

const LANG_LABELS: Record<string, string> = {
  'hi': 'Hindi',
  'en': 'English',
  'hi-en': 'Hindi / English',
  'hi-IN': 'Hindi',
}

function CoachingPanel({ analysis }: { analysis: CallAnalysis }) {
  if (analysis.parse_error) {
    return (
      <div className="px-4 pb-3 pt-2" style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}>
        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>AI analysis unavailable — JSON parse error.</p>
      </div>
    )
  }

  const score = analysis.agent_score
  const scoreRows = score
    ? [
        { label: 'Greeting',           val: score.greeting },
        { label: 'Product knowledge',  val: score.product_knowledge },
        { label: 'Objection handling', val: score.objection_handling },
        { label: 'Closing',            val: score.closing },
      ]
    : []

  const durationLabel = analysis.duration_seconds
    ? `${Math.floor(analysis.duration_seconds / 60)}m ${analysis.duration_seconds % 60}s`
    : null

  return (
    <div
      className="px-4 pb-5 pt-3 space-y-4"
      style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}
    >
      <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
        <BarChart2 className="h-3 w-3" /> AI Analysis
      </p>

      {/* Summary metrics strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {analysis.customer_sentiment && (
          <MetricCard label="Customer sentiment">
            <span className={`${SENTIMENT_STYLES[analysis.customer_sentiment]?.cls ?? 'badge-neutral'} text-xs`}>
              {SENTIMENT_STYLES[analysis.customer_sentiment]?.label ?? analysis.customer_sentiment}
            </span>
          </MetricCard>
        )}
        {analysis.customer_intent && (
          <MetricCard label="Customer intent">
            <span className="text-sm font-medium capitalize" style={{ color: 'var(--text-primary)' }}>
              {analysis.customer_intent.replace(/_/g, ' ')}
            </span>
          </MetricCard>
        )}
        {analysis.language && (
          <MetricCard label="Language">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {LANG_LABELS[analysis.language] ?? analysis.language.toUpperCase()}
            </span>
          </MetricCard>
        )}
        {durationLabel && (
          <MetricCard label="Duration">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{durationLabel}</span>
          </MetricCard>
        )}
      </div>

      {/* Agent score card */}
      {score && (
        <div
          className="rounded-lg p-3"
          style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)' }}
        >
          <div className="flex items-center gap-4 mb-3">
            <div
              className="w-14 h-14 rounded-full flex flex-col items-center justify-center flex-shrink-0"
              style={{ border: '3px solid var(--brand-primary)', background: 'var(--brand-50)' }}
            >
              <span className="text-xl font-medium leading-none" style={{ color: 'var(--brand-700)' }}>
                {score.overall}
              </span>
              <span className="text-xs" style={{ color: 'var(--brand-600)' }}>/5</span>
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Overall agent score</p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Across {scoreRows.length} dimensions</p>
            </div>
          </div>
          <div className="space-y-2">
            {scoreRows.map(({ label, val }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <ScoreBar value={val} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coaching notes */}
      {analysis.coaching_notes && (
        <div
          className="rounded-r-lg px-3 py-2.5 text-sm"
          style={{
            borderLeft: '3px solid var(--brand-primary)',
            background: 'var(--surface-muted)',
            color: 'var(--text-primary)',
            lineHeight: '1.6',
          }}
        >
          <p className="text-xs font-medium mb-1 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
            <Lightbulb className="h-3 w-3" /> Coaching notes
          </p>
          {analysis.coaching_notes}
        </div>
      )}

      {/* Training flags */}
      {(analysis.training_flags ?? []).length > 0 && (
        <div>
          <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Training flags</p>
          <div className="flex flex-wrap gap-1.5">
            {analysis.training_flags!.map(flag => (
              <span key={flag} className="badge-warning text-xs">{flag.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      )}

      {/* Key topics */}
      {(analysis.key_topics ?? []).length > 0 && (
        <div>
          <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Key topics</p>
          <div className="flex flex-wrap gap-1.5">
            {analysis.key_topics!.map(t => (
              <span
                key={t}
                className="text-xs px-2 py-0.5 rounded"
                style={{
                  background: 'var(--surface-muted)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Follow-up action */}
      {analysis.follow_up_action && (
        <div
          className="rounded-lg px-3 py-2.5 text-sm"
          style={{ background: 'var(--brand-50)', color: 'var(--brand-800)' }}
        >
          <span className="font-medium">Next step: </span>
          {analysis.follow_up_action}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Supporting components
// ---------------------------------------------------------------------------

function ExotelPayloadPanel({ payload }: { payload: Record<string, string> }) {
  const primary = PAYLOAD_PRIMARY_KEYS.filter(k => k in payload)
  const rest = Object.keys(payload).filter(k => !PAYLOAD_PRIMARY_KEYS.includes(k))
  return (
    <div className="px-4 pb-3 pt-1" style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}>
      <p className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
        <Terminal className="h-3 w-3" /> Exotel Webhook Payload
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs font-mono">
        {primary.map(k => (
          <div key={k} className="flex gap-1 min-w-0">
            <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>{k}:</span>
            <span className="truncate" style={{ color: 'var(--text-primary)' }} title={payload[k]}>{payload[k] || '—'}</span>
          </div>
        ))}
        {rest.map(k => (
          <div key={k} className="flex gap-1 min-w-0">
            <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>{k}:</span>
            <span className="truncate" style={{ color: 'var(--text-secondary)' }} title={payload[k]}>{payload[k] || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

const SOURCE_BADGE: Record<string, { label: string; cls: string }> = {
  [CallSource.EXOTEL]:         { label: 'Exotel',  cls: 'badge-info'   },
  [CallSource.MANUAL_MOBILE]:  { label: 'Mobile',  cls: 'badge-purple' },
  [CallSource.MANUAL_DESKTOP]: { label: 'Manual',  cls: 'badge-orange' },
}

function CallSourceBadge({ source }: { source?: CallSource }) {
  if (!source) return <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>
  const s = SOURCE_BADGE[source]
  if (!s) return <span className="text-xs">{source}</span>
  return <span className={`${s.cls} text-xs`}>{s.label}</span>
}

function DispositionBadge({ disposition }: { disposition: string | null }) {
  if (!disposition) return <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>
  return (
    <span className="badge-neutral">
      {DISPOSITION_LABELS[disposition] ?? disposition.replace(/_/g, ' ')}
    </span>
  )
}

function ReminderCard({ reminder, onNavigate }: { reminder: Reminder; onNavigate: (leadId: number) => void }) {
  const due = new Date(reminder.reminder_at)
  const isOverdue = due < new Date()
  return (
    <div
      className="flex items-start justify-between gap-3 p-3 rounded-md border cursor-pointer transition-colors"
      style={{
        borderColor: isOverdue ? 'var(--danger-border)' : 'var(--border-default)',
        background: isOverdue ? 'var(--danger-bg)' : 'transparent',
      }}
      onClick={() => onNavigate(reminder.lead_id)}
    >
      <Bell className="h-4 w-4 shrink-0 mt-0.5" style={{ color: isOverdue ? 'var(--danger-text)' : 'var(--brand-primary)' }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Lead #{reminder.lead_id}</p>
        {reminder.note && (
          <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>{reminder.note}</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs font-medium" style={{ color: isOverdue ? 'var(--danger-text)' : 'var(--text-tertiary)' }}>
          {isOverdue ? 'Overdue' : 'Due'}
        </p>
        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {due.toLocaleDateString()} {due.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Expand icon helper
// ---------------------------------------------------------------------------

function ExpandIcon({ call, expanded }: { call: CallLog; expanded: boolean }) {
  if (expanded) return <ChevronUp className="h-3.5 w-3.5" />
  if (call.analysis) return <Lightbulb className="h-3.5 w-3.5" style={{ color: 'var(--brand-primary)' }} />
  if (call.transcript) return <MessageSquare className="h-3.5 w-3.5" />
  return <ChevronDown className="h-3.5 w-3.5" />
}

// ---------------------------------------------------------------------------
// Re-analyze guard
// ---------------------------------------------------------------------------

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

function isCallExpired(call: CallLog): boolean {
  return Date.now() - new Date(call.created_at).getTime() > THIRTY_DAYS_MS
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CallHistoryPage() {
  const navigate = useNavigate()
  const [calls, setCalls] = useState<CallLog[]>([])
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [loading, setLoading] = useState(true)
  const [showReminders, setShowReminders] = useState(true)
  const [filterDisposition, setFilterDisposition] = useState('')
  const [filterSource, setFilterSource] = useState('')

  const [reanalyzingIds, setReanalyzingIds] = useState<Set<number>>(new Set())

  const role = authService.getCurrentUser()?.role
  const isManager = role === UserRole.TEAM_LEADER
    || role === UserRole.INSURANCE_HEAD
    || role === UserRole.DEALER_PRINCIPAL

  const handleReanalyze = async (callId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setReanalyzingIds(s => new Set(s).add(callId))
    try {
      await telephonyService.reanalyzeCall(callId)
      // Refresh the single row so the "Reanalyzing…" badge appears quickly,
      // then let the full reload pick up the final analysis.
      await load()
    } catch {
      // 410 means recording expired — the button is already disabled for that case;
      // other errors are transient, a silent failure is acceptable here.
    } finally {
      setReanalyzingIds(s => { const n = new Set(s); n.delete(callId); return n })
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      const [logs, rems] = await Promise.all([
        isManager ? telephonyService.getAllCallLogs() : telephonyService.getMyCallLogs(),
        telephonyService.getMyReminders(),
      ])
      setCalls(logs)
      setReminders(rems)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const filteredCalls = calls.filter(c => {
    if (filterDisposition && c.disposition !== filterDisposition) return false
    if (filterSource && c.source !== filterSource) return false
    return true
  })

  const uniqueDispositions = [...new Set(calls.map(c => c.disposition).filter(Boolean))] as string[]

  const colSpan = 9
  const isMobile = useWindowWidth() < 768
  const [expandedCallId, setExpandedCallId] = useState<number | null>(null)

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Phone className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            Call History
            <button onClick={load} disabled={loading} className="btn-icon" title="Refresh" aria-label="Refresh" style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">
            {loading
              ? 'Loading…'
              : `${calls.length} recent calls · ${reminders.length} pending reminder${reminders.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {reminders.length > 0 && (
        <div className="card">
          <button className="flex items-center justify-between w-full" onClick={() => setShowReminders(v => !v)}>
            <h2 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Bell className="h-5 w-5" style={{ color: 'var(--brand-600)' }} />
              Pending Reminders
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--brand-primary)', color: '#fff' }}>
                {reminders.length}
              </span>
            </h2>
            {showReminders
              ? <ChevronUp className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
              : <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />}
          </button>
          {showReminders && (
            <div className="mt-3 space-y-2">
              {reminders.map(r => (
                <ReminderCard key={r.id} reminder={r} onNavigate={id => navigate(`/leads/${id}`)} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="table-card">
        <div className="table-card-header">
          <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Recent Calls</h2>
          <div className="flex items-center gap-2">
            <select
              className="input"
              style={{ width: 'auto' }}
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
            >
              <option value="">All sources</option>
              <option value={CallSource.EXOTEL}>Exotel</option>
              <option value={CallSource.MANUAL_MOBILE}>Mobile</option>
              <option value={CallSource.MANUAL_DESKTOP}>Manual</option>
            </select>
            <select
              className="input"
              style={{ width: 'auto' }}
              value={filterDisposition}
              onChange={e => setFilterDisposition(e.target.value)}
            >
              <option value="">All dispositions</option>
              {uniqueDispositions.map(d => (
                <option key={d} value={d}>{DISPOSITION_LABELS[d] ?? d.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        </div>
        {isMobile ? (
          loading ? (
            <div className="py-10 flex justify-center"><div className="spinner" /></div>
          ) : filteredCalls.length === 0 ? (
            <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No calls yet</p></div>
          ) : (
            <div className="flex flex-col gap-2 p-3">
              {filteredCalls.map(call => {
                const expanded = expandedCallId === call.id
                const sentiment = call.analysis?.customer_sentiment
                return (
                  <div key={call.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div
                      className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                      onClick={() => setExpandedCallId(expanded ? null : call.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" style={{ color: 'var(--brand-primary)' }}>Lead #{call.lead_id}</p>
                        <DispositionBadge disposition={call.disposition} />
                      </div>
                      <span className="text-xs shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                        {call.duration_seconds != null ? `${call.duration_seconds}s` : '—'}
                      </span>
                      <ChevronDown
                        className="h-4 w-4 shrink-0 transition-transform"
                        style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                      />
                    </div>
                    {expanded && (
                      <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                        <div className="flex justify-between text-xs py-1">
                          <span style={{ color: 'var(--text-tertiary)' }}>Source</span>
                          <CallSourceBadge source={call.source} />
                        </div>
                        {sentiment && (
                          <div className="flex justify-between text-xs py-1">
                            <span style={{ color: 'var(--text-tertiary)' }}>Sentiment</span>
                            <span className={SENTIMENT_STYLES[sentiment]?.cls ?? 'badge-neutral'}>
                              {SENTIMENT_STYLES[sentiment]?.label ?? sentiment}
                            </span>
                          </div>
                        )}
                        {call.od_discount_offered != null && (
                          <div className="flex justify-between text-xs py-1">
                            <span style={{ color: 'var(--text-tertiary)' }}>OD Discount</span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{call.od_discount_offered}%</span>
                          </div>
                        )}
                        <div className="flex justify-between text-xs py-1">
                          <span style={{ color: 'var(--text-tertiary)' }}>When</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{new Date(call.created_at).toLocaleString()}</span>
                        </div>
                        {call.remarks && (
                          <p className="text-xs mt-2 py-1" style={{ color: 'var(--text-tertiary)' }}>{call.remarks}</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button
                            className="flex-1 text-xs py-2 rounded-md"
                            style={{ background: 'var(--brand-primary)', color: 'white' }}
                            onClick={e => { e.stopPropagation(); navigate(`/leads/${call.lead_id}`) }}
                          >
                            View Lead
                          </button>
                          {isManager && (call.transcript || call.analysis) && (
                            <button
                              className="flex-1 text-xs py-2 rounded-md border"
                              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
                              onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === call.id ? null : call.id) }}
                            >
                              {call.analysis ? 'Coaching' : 'Transcript'}
                            </button>
                          )}
                          {isManager && call.recording_url && (() => {
                            const expired = isCallExpired(call)
                            const busy    = reanalyzingIds.has(call.id)
                            return (
                              <button
                                className="flex-1 text-xs py-2 rounded-md border"
                                disabled={expired || busy}
                                title={expired ? 'Recording expired — 30-day S3 lifecycle reached' : 'Re-run AI analysis on recording'}
                                style={{
                                  borderColor: 'var(--border-default)',
                                  color: expired ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                                  opacity: expired ? 0.5 : 1,
                                }}
                                onClick={e => handleReanalyze(call.id, e)}
                              >
                                {busy ? '…' : 'Re-analyze'}
                              </button>
                            )
                          })()}
                        </div>
                        {expandedRow === call.id && call.transcript && <TranscriptPanel transcript={call.transcript} />}
                        {expandedRow === call.id && call.analysis && <CoachingPanel analysis={call.analysis} />}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Disposition</th>
                  <th className="hidden sm:table-cell">Source</th>
                  <th className="hidden sm:table-cell">Sentiment</th>
                  <th>Duration</th>
                  <th className="hidden sm:table-cell">OD Discount</th>
                  <th className="hidden md:table-cell">Remarks</th>
                  <th>When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={colSpan} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>Loading…</td></tr>
                ) : filteredCalls.length === 0 ? (
                  <tr><td colSpan={colSpan} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>No calls yet</td></tr>
                ) : (
                  filteredCalls.map(call => (
                    <React.Fragment key={call.id}>
                      <tr className="clickable" onClick={() => navigate(`/leads/${call.lead_id}`)}>
                        <td className="text-primary-data">#{call.lead_id}</td>
                        <td><DispositionBadge disposition={call.disposition} /></td>
                        <td className="hidden sm:table-cell">
                          <CallSourceBadge source={call.source} />
                        </td>
                        <td className="hidden sm:table-cell">
                          {call.analysis?.customer_sentiment ? (
                            <span className={`${SENTIMENT_STYLES[call.analysis.customer_sentiment]?.cls ?? 'badge-neutral'}`}>
                              {SENTIMENT_STYLES[call.analysis.customer_sentiment]?.label ?? call.analysis.customer_sentiment}
                            </span>
                          ) : (
                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-tertiary)' }}>
                          {call.duration_seconds != null ? `${call.duration_seconds}s` : '—'}
                        </td>
                        <td className="hidden sm:table-cell">{call.od_discount_offered != null ? `${call.od_discount_offered}%` : '—'}</td>
                        <td className="hidden md:table-cell max-w-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                          {call.remarks || '—'}
                        </td>
                        <td className="whitespace-nowrap text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {new Date(call.created_at).toLocaleString()}
                        </td>
                        <td>
                          <div className="flex items-center gap-1 justify-end">
                            {isManager && call.recording_url && (() => {
                              const expired = isCallExpired(call)
                              const busy    = reanalyzingIds.has(call.id)
                              return (
                                <button
                                  className="btn-icon"
                                  disabled={expired || busy}
                                  title={expired ? 'Recording expired — 30-day S3 lifecycle reached' : 'Re-run AI analysis on recording'}
                                  onClick={e => handleReanalyze(call.id, e)}
                                  style={{ opacity: expired ? 0.35 : 1 }}
                                >
                                  <RotateCcw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
                                </button>
                              )
                            })()}
                            {isManager && (call.transcript || call.analysis || call.exotel_payload) && (
                              <button
                                className="btn-icon"
                                title={call.analysis ? 'Show coaching' : call.transcript ? 'Show transcript' : 'Show payload'}
                                onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === call.id ? null : call.id) }}
                              >
                                <ExpandIcon call={call} expanded={expandedRow === call.id} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expandedRow === call.id && (
                        <>
                          {call.transcript && (
                            <tr key={`${call.id}-t`}>
                              <td colSpan={colSpan} style={{ padding: 0 }}>
                                <TranscriptPanel transcript={call.transcript} />
                              </td>
                            </tr>
                          )}
                          {call.analysis && (
                            <tr key={`${call.id}-a`}>
                              <td colSpan={colSpan} style={{ padding: 0 }}>
                                <CoachingPanel analysis={call.analysis} />
                              </td>
                            </tr>
                          )}
                          {call.exotel_payload && (
                            <tr key={`${call.id}-p`}>
                              <td colSpan={colSpan} style={{ padding: 0 }}>
                                <ExotelPayloadPanel payload={call.exotel_payload} />
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
